import { FeatureRow } from "./subscriptionPlans";
import {
  ARCHITECT_IMAGE_LIMITS,
  ARCHITECT_PDF_LIMITS,
  REGULAR_DOC_IMAGE_LIMITS,
} from "./constants";
import { formatFileSize } from "@/lib/utils";
import type { SubscriptionPlan } from "@/server/types/subscription";

/**
 * Render an advertised limit row FROM the values the client actually enforces
 * (the tables in constants.ts).
 *
 * Derived rather than hand-typed on purpose: these rows previously listed
 * ladders that no code read, so the page advertised 100 KB-10 MB for PDFs while
 * the app enforced a flat 100 MB for everyone (20 MB until #1139 raised it),
 * and advertised 5-10 MB for images against a flat 40 MB. Deriving them makes
 * that drift impossible — change a limit and the copy follows.
 */
const advertise = (
  limits: Record<SubscriptionPlan, number>,
): Record<SubscriptionPlan, string> =>
  Object.fromEntries(
    (Object.keys(limits) as SubscriptionPlan[]).map((plan) => [
      plan,
      `Up to ${formatFileSize(limits[plan], 0)}`,
    ]),
  ) as Record<SubscriptionPlan, string>;

const architectPdfValues = advertise(ARCHITECT_PDF_LIMITS);
const architectImageValues = advertise(ARCHITECT_IMAGE_LIMITS);
// Regular-chat rows, derived from the same matrix the client enforces. PDF and
// image share one ladder; video carries the headroom.
const regularFileValues = advertise(REGULAR_DOC_IMAGE_LIMITS);

export const FEATURE_ROWS: FeatureRow[] = [
  // ─── ORION Architect ─────────────────────────────────────────────────────
  {
    category: "ORION Architect",
    name: "Polymath BFN™ Calculator",
    // NOTE: the free value is deliberately "During trial", not `true`. Access
    // is `active subscription OR active trial` — enforced server-side, in the
    // ow2-chat-be repo, by `isBfnEntitled()`
    // (src/modules/bfn/application/bfnEntitlement.ts), which the BFN token
    // exchange calls. PlanFeatureTable's default "differences only" view also
    // hides any row whose values are identical across every plan, so an
    // all-`true` row would be invisible on the page it is meant to advertise.
    info: "Included with any active plan — running calculations does not use credits. Free members can use it while their trial credits last.",
    values: {
      free: "During trial",
      standard: true,
      pro: true,
      proplus: true,
      premium: true,
    },
  },
  {
    category: "ORION Architect",
    name: "Monthly credits",
    info: "Monthly credits reset each billing period and do not roll over",
    values: {
      free: "—",
      standard: "222 credits/mo",
      pro: "444 credits/mo",
      proplus: "888 credits/mo",
      premium: "2,222 credits/mo",
    },
  },
  {
    category: "ORION Architect",
    name: "Credits",
    hidden: true,
    info: "Monthly ORION Architect credits",
    values: {
      free: "—",
      standard: "222 / mo",
      pro: "444 / mo",
      proplus: "888 / mo",
      premium: "2,222 / mo",
    },
  },
  {
    category: "ORION Architect",
    name: "Prompt size",
    hidden: true,
    info: "Maximum characters per prompt",
    values: {
      free: "1,000 chars",
      standard: "4,000 chars",
      pro: "10,000 chars",
      proplus: "10,000 chars",
      premium: "30,000 chars",
    },
  },
  {
    category: "ORION Architect",
    name: "Voice interactions",
    hidden: true,
    info: "AI talking back to the user (text-to-speech responses)",
    values: {
      free: "10 prompts (lifetime)",
      standard: true,
      pro: true,
      proplus: true,
      premium: true,
    },
  },
  {
    category: "ORION Architect",
    name: "Long-term memory",
    hidden: true,
    values: {
      free: false,
      standard: false,
      pro: false,
      proplus: false,
      premium: true,
    },
  },

  {
    category: "ORION Architect",
    name: "Priority access",
    info: "Server priority during high demand — lower tier number = higher priority",
    values: {
      free: "—",
      standard: "—",
      pro: "—",
      proplus: "—",
      premium: "Included",
    },
  },
  {
    category: "ORION Architect",
    name: "PDF attachment limit",
    info: "Maximum PDF size you can upload for OA to analyze. OA can only analyze PDFs you share — it cannot create or generate them.",
    // Derived from the enforced ladder — see architectPdfValues above.
    values: architectPdfValues,
  },
  {
    category: "ORION Architect",
    name: "Image sharing limit",
    info: "Maximum image size for ORION Architect Access",
    // Derived from the enforced ladder — see `advertise` above.
    values: architectImageValues,
  },

  // ─── 💬 Core Messaging ───────────────────────────────────────────────────
  {
    category: "Messaging",
    name: "1-1 Messaging",
    values: {
      free: true,
      standard: true,
      pro: true,
      proplus: true,
      premium: true,
    },
  },
  {
    category: "Messaging",
    name: "Group chats",
    info: "Max number of users in a group chat",
    values: {
      free: "Up to 500",
      standard: "Up to 1,250",
      pro: "Unlimited",
      proplus: "Unlimited",
      premium: "Unlimited",
    },
  },
  {
    category: "Messaging",
    name: "Voice notes",
    info: "Max duration per voice note",
    values: {
      free: "60 sec",
      standard: "3 min",
      pro: "6 min",
      proplus: "8 min",
      premium: "10 min",
    },
  },
  {
    category: "Messaging",
    name: "Voice mode",
    hidden: true,
    info: "Max duration per AI voice session",
    values: {
      free: false,
      standard: "3 min",
      pro: "6 min",
      proplus: "8 min",
      premium: "10 min",
    },
  },
  {
    category: "Messaging",
    name: "Threads",
    values: {
      free: true,
      standard: true,
      pro: true,
      proplus: true,
      premium: true,
    },
  },
  {
    category: "Messaging",
    name: "End-to-end encryption",
    values: {
      free: true,
      standard: true,
      pro: true,
      proplus: true,
      premium: true,
    },
  },

  {
    category: "Messaging",
    name: "File sharing",
    info: "Maximum size per photo or document shared in a chat",
    // Derived from REGULAR_DOC_IMAGE_LIMITS — see `advertise` above.
    values: regularFileValues,
  },
  // NO "Video sharing" row on purpose. The video ladder (REGULAR_VIDEO_LIMITS:
  // pro 200 / pro+ 400 / premium 1000) is still ENFORCED, but its per-tier
  // headroom is only deliverable once chunked upload ships (flag-off it clamps
  // to 100 MB). Advertising it now would over-claim. Re-add this row, derived
  // from REGULAR_VIDEO_LIMITS, when chunked upload lands.
  {
    category: "Messaging",
    name: "File size limit",
    hidden: true,
    info: "Maximum size per shared file",
    // Derived — mirrors the visible "File sharing" row.
    values: regularFileValues,
  },
  // ─── 🎥 Video Calls ──────────────────────────────────────────────────────
  {
    category: "Video Calls",
    name: "1-1 video calls",
    values: {
      free: true,
      standard: true,
      pro: true,
      proplus: true,
      premium: true,
    },
  },
  {
    category: "Video Calls",
    name: "Group video calls",
    values: {
      free: true,
      standard: true,
      pro: true,
      proplus: true,
      premium: true,
    },
  },
  {
    category: "Video Calls",
    name: "Max participants",
    info: "Maximum users on a single call",
    values: {
      free: "Up to 10",
      standard: "Up to 300",
      pro: "Up to 400",
      proplus: "Up to 500",
      premium: "Up to 500",
    },
  },
  {
    category: "Video Calls",
    name: "Meeting hours / month",
    values: {
      free: "45 min / call",
      standard: "16 hrs/mo",
      pro: "20 hrs/mo",
      proplus: "24 hrs/mo",
      premium: "30 hrs/mo",
    },
  },
];
